import { Loader2, MailPlus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { EmptyState, ErrorBanner } from "@/components/empty-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  addMemberAction,
  removeMemberAction,
  updateMemberRoleAction,
} from "@/features/teams/actions";
import { api } from "@/lib/api";
import {
  TEAM_ROLES,
  type TeamMember,
  type TeamMembersResponse,
  type TeamResponse,
  type TeamRole,
  type UserSummary,
} from "@/lib/types";

interface TeamTabProps {
  team: TeamResponse;
  isAdmin: boolean;
  /**
   * The currently signed-in user. Needed to (a) protect "remove yourself"
   * with a different label, (b) guard against demoting your own admin row,
   * which the backend last-admin check would also reject.
   */
  me: UserSummary;
  onUpdated(next: TeamResponse): void;
}

export function TeamTab({ team, isAdmin, me, onUpdated }: TeamTabProps) {
  const [name, setName] = useState(team.name);
  const [saving, setSaving] = useState(false);
  const [members, setMembers] = useState<TeamMember[] | null>(null);
  const [membersError, setMembersError] = useState<string | null>(null);

  // Derived from `members`; used to gate UI for last-admin logic. The
  // backend is still the source of truth — we mirror the rule here so the
  // disabled state matches reality and toasts make sense.
  const adminCount = members?.filter((m) => m.role === "team_admin").length ?? 0;

  useEffect(() => {
    // The /teams/current/members endpoint resolves the team from the
    // kosToken (not from this id), but we still want to refetch when the
    // active team changes. Touching `team.id` here keeps the dep list
    // honest and silences `useExhaustiveDependencies`.
    void team.id;
    let cancelled = false;
    setMembersError(null);
    api<TeamMembersResponse>("/teams/current/members")
      .then((res) => {
        if (!cancelled) setMembers(res.items);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setMembers([]);
        setMembersError(err instanceof Error ? err.message : "Failed to load members");
      });
    return () => {
      cancelled = true;
    };
  }, [team.id]);

  const refreshMembers = async (): Promise<void> => {
    try {
      const res = await api<TeamMembersResponse>("/teams/current/members");
      setMembers(res.items);
    } catch (err) {
      setMembersError(err instanceof Error ? err.message : "Failed to refresh members");
    }
  };

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || trimmed === team.name || saving) return;
    setSaving(true);
    try {
      const next = await api<TeamResponse>("/teams/current", {
        method: "PATCH",
        body: JSON.stringify({ name: trimmed }),
      });
      onUpdated(next);
      toast.success("Team updated");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to update team");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Team</CardTitle>
          <CardDescription>The team your work is scoped to.</CardDescription>
        </CardHeader>
        <CardContent>
          <form className="grid max-w-md gap-4" onSubmit={onSubmit}>
            <div className="space-y-1.5">
              <Label htmlFor="team-slug">Slug</Label>
              <Input id="team-slug" value={team.slug} disabled readOnly />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="team-name">Name</Label>
              <Input
                id="team-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                disabled={!isAdmin}
                placeholder="Team name"
                maxLength={200}
              />
              {!isAdmin ? (
                <p className="text-xs text-muted-foreground">
                  Only team admins can rename the team.
                </p>
              ) : null}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline">role: {team.my_role}</Badge>
              <Badge variant="outline">members: {team.member_count}</Badge>
            </div>
            {isAdmin ? (
              <div>
                <Button
                  type="submit"
                  disabled={saving || !name.trim() || name.trim() === team.name}
                >
                  {saving ? "Saving…" : "Save changes"}
                </Button>
              </div>
            ) : null}
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Members</CardTitle>
          <CardDescription>
            People who can use this team's data and agents.{" "}
            {isAdmin
              ? "Add members by email — they'll see the team in their workspace switcher on next sign-in."
              : null}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isAdmin ? <AddMemberForm onAdded={refreshMembers} /> : null}

          {membersError ? <ErrorBanner>{membersError}</ErrorBanner> : null}
          {members === null ? (
            <div className="space-y-2">
              <Skeleton className="h-8" />
              <Skeleton className="h-8" />
              <Skeleton className="h-8" />
            </div>
          ) : members.length === 0 ? (
            <EmptyState>No members yet.</EmptyState>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Joined</TableHead>
                  {isAdmin || members.some((m) => m.user_id === me.id) ? (
                    <TableHead className="text-right">Actions</TableHead>
                  ) : null}
                </TableRow>
              </TableHeader>
              <TableBody>
                {members.map((member) => (
                  <MemberRow
                    key={member.user_id}
                    member={member}
                    isAdmin={isAdmin}
                    me={me}
                    adminCount={adminCount}
                    onChanged={refreshMembers}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// AddMemberForm
// ────────────────────────────────────────────────────────────────────

function AddMemberForm({ onAdded }: { onAdded: () => Promise<void> | void }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<TeamRole>("team_editor");
  const [pending, setPending] = useState(false);

  const submit = async (): Promise<void> => {
    setPending(true);
    try {
      const result = await addMemberAction(email, role);
      if (result.ok) {
        toast.success(result.message);
        setEmail("");
        await onAdded();
      } else {
        toast.error(result.message);
      }
    } finally {
      setPending(false);
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
      className="flex flex-wrap items-end gap-2"
    >
      <div className="flex-1 space-y-1.5 sm:min-w-[260px]">
        <Label htmlFor="invite-email">Invite by email</Label>
        <Input
          id="invite-email"
          type="email"
          placeholder="teammate@company.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={pending}
        />
      </div>
      <div className="w-[140px] space-y-1.5">
        <Label htmlFor="invite-role">Role</Label>
        <Select value={role} onValueChange={(v) => setRole(v as TeamRole)} disabled={pending}>
          <SelectTrigger id="invite-role">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TEAM_ROLES.map((r) => (
              <SelectItem key={r} value={r}>
                {humanizeRole(r)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Button type="submit" disabled={pending || !email.trim()}>
        {pending ? (
          <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
        ) : (
          <MailPlus className="mr-1.5 h-3.5 w-3.5" />
        )}
        Add member
      </Button>
    </form>
  );
}

// ────────────────────────────────────────────────────────────────────
// MemberRow
// ────────────────────────────────────────────────────────────────────

function MemberRow({
  member,
  isAdmin,
  me,
  adminCount,
  onChanged,
}: {
  member: TeamMember;
  isAdmin: boolean;
  me: UserSummary;
  adminCount: number;
  onChanged: () => Promise<void> | void;
}) {
  const [pending, setPending] = useState(false);
  const isMe = member.user_id === me.id;
  const isLastAdmin = member.role === "team_admin" && adminCount <= 1;
  // Self can always remove (subject to last-admin guard); admins can manage anyone.
  const canRemove = (isAdmin || isMe) && !isLastAdmin;
  const canChangeRole = isAdmin && !isLastAdmin;

  const onRoleChange = async (next: string): Promise<void> => {
    if (next === member.role) return;
    setPending(true);
    try {
      const result = await updateMemberRoleAction(member.user_id, next);
      if (result.ok) {
        toast.success(result.message);
        await onChanged();
      } else {
        toast.error(result.message);
      }
    } finally {
      setPending(false);
    }
  };

  const onRemove = async (): Promise<void> => {
    const label = isMe ? "Leave this team?" : `Remove ${member.email}?`;
    if (!window.confirm(label)) return;
    setPending(true);
    try {
      const result = await removeMemberAction(member.user_id);
      if (result.ok) {
        toast.success(result.message);
        await onChanged();
      } else {
        toast.error(result.message);
      }
    } finally {
      setPending(false);
    }
  };

  return (
    <TableRow>
      <TableCell className="font-mono text-xs">
        {member.email}
        {isMe ? <span className="ml-1 text-muted-foreground">(you)</span> : null}
      </TableCell>
      <TableCell>{member.name ?? "—"}</TableCell>
      <TableCell>
        {canChangeRole ? (
          <Select
            value={member.role}
            onValueChange={(v) => void onRoleChange(v)}
            disabled={pending}
          >
            <SelectTrigger className="h-7 w-[110px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TEAM_ROLES.map((r) => (
                <SelectItem key={r} value={r}>
                  {humanizeRole(r)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Badge variant="secondary">{humanizeRole(member.role)}</Badge>
        )}
      </TableCell>
      <TableCell className="text-xs text-muted-foreground">
        {new Date(member.joined_at).toLocaleDateString()}
      </TableCell>
      {isAdmin || isMe ? (
        <TableCell className="text-right">
          {canRemove ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => void onRemove()}
              disabled={pending}
              className="h-7 px-2 text-destructive hover:text-destructive"
            >
              <Trash2 className="mr-1 h-3.5 w-3.5" />
              {isMe ? "Leave" : "Remove"}
            </Button>
          ) : isLastAdmin ? (
            <span className="text-xs text-muted-foreground">last admin</span>
          ) : null}
        </TableCell>
      ) : null}
    </TableRow>
  );
}

function humanizeRole(role: string): string {
  if (role === "team_admin") return "Admin";
  if (role === "team_editor") return "Editor";
  if (role === "team_viewer") return "Viewer";
  return role;
}
